import { useMemo, useRef, useState, type DragEvent, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { api, putWithWriteSas } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { PageShell } from '../components/BrandChrome'
import { Alert, Button, Card, FieldError, Input, Label, Subtitle, Title } from '../components/ui'
import { errorMessage, formatBytes } from '../lib/format'
import { isValidE164, normalizePhoneInput } from '../lib/phone'
import { sha256Hex } from '../lib/sha256'

type PickedFile = {
  file: File
  relativePath: string
}

const DEFAULT_TTL = 72
const MIN_TTL = 1
const MAX_TTL = 14 * 24
/** Prepare/upload this many objects per grant batch (write SAS TTL is 15m). */
const PREPARE_BATCH_SIZE = 8

function relativeFromFile(file: File): string {
  const withPath = file as File & { webkitRelativePath?: string }
  const rel = withPath.webkitRelativePath?.replace(/\\/g, '/').trim()
  if (rel) return rel
  return file.name
}

type FsEntry = FileSystemEntry & {
  isFile: boolean
  isDirectory: boolean
  name: string
}

type FsFileEntry = FsEntry & {
  file: (ok: (f: File) => void, err?: (e: Error) => void) => void
}

type FsDirEntry = FsEntry & {
  createReader: () => {
    readEntries: (
      ok: (entries: FileSystemEntry[]) => void,
      err?: (e: Error) => void,
    ) => void
  }
}

async function readAllDirectoryEntries(dir: FsDirEntry): Promise<FileSystemEntry[]> {
  const reader = dir.createReader()
  const all: FileSystemEntry[] = []
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject)
    })
    if (!batch.length) break
    all.push(...batch)
  }
  return all
}

async function walkEntry(entry: FileSystemEntry, pathPrefix: string, out: PickedFile[]): Promise<void> {
  const e = entry as FsEntry
  if (e.isFile) {
    const file = await new Promise<File>((resolve, reject) => {
      ;(e as FsFileEntry).file(resolve, reject)
    })
    const relativePath = pathPrefix ? `${pathPrefix}/${file.name}` : file.name
    out.push({ file, relativePath: relativePath.replace(/\\/g, '/') })
    return
  }
  if (e.isDirectory) {
    const nextPrefix = pathPrefix ? `${pathPrefix}/${e.name}` : e.name
    const children = await readAllDirectoryEntries(e as FsDirEntry)
    for (const child of children) {
      await walkEntry(child, nextPrefix, out)
    }
  }
}

async function pickedFromDataTransfer(dt: DataTransfer): Promise<PickedFile[]> {
  const items = dt.items
  const entries: FileSystemEntry[] = []
  if (items?.length) {
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry?.()
      if (entry) entries.push(entry)
    }
  }
  if (entries.length) {
    const out: PickedFile[] = []
    for (const entry of entries) {
      await walkEntry(entry, '', out)
    }
    return out
  }
  return Array.from(dt.files ?? []).map((file) => ({
    file,
    relativePath: relativeFromFile(file),
  }))
}

export function DropNewPage() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)

  const [phone, setPhone] = useState('')
  const [ttl, setTtl] = useState(DEFAULT_TTL)
  const [files, setFiles] = useState<PickedFile[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [shareUrl, setShareUrl] = useState<string | null>(null)

  const phoneNorm = useMemo(() => normalizePhoneInput(phone), [phone])
  const phoneOk = isValidE164(phoneNorm)
  const totalBytes = useMemo(() => files.reduce((s, f) => s + f.file.size, 0), [files])

  function mergePicked(next: PickedFile[]) {
    setFiles((prev) => {
      const map = new Map(prev.map((p) => [p.relativePath, p]))
      for (const item of next) map.set(item.relativePath, item)
      return Array.from(map.values()).sort((a, b) => a.relativePath.localeCompare(b.relativePath))
    })
  }

  function addFiles(list: FileList | File[]) {
    mergePicked(
      Array.from(list).map((file) => ({
        file,
        relativePath: relativeFromFile(file),
      })),
    )
  }

  async function onDropZone(e: DragEvent) {
    e.preventDefault()
    setDragOver(false)
    try {
      const picked = await pickedFromDataTransfer(e.dataTransfer)
      if (picked.length) mergePicked(picked)
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setShareUrl(null)
    if (!phoneOk) {
      setError('Enter a valid phone number in E.164 form (example +15551234567).')
      return
    }
    if (files.length === 0) {
      setError('Add at least one file or folder.')
      return
    }
    if (ttl < MIN_TTL || ttl > MAX_TTL) {
      setError(`TTL must be between ${MIN_TTL} and ${MAX_TTL} hours.`)
      return
    }

    setBusy(true)
    try {
      setProgress('Creating drop…')
      await api.me()
      const drop = await api.createDrop(phoneNorm, ttl)
      const dropId = drop.drop_id

      for (let start = 0; start < files.length; start += PREPARE_BATCH_SIZE) {
        const batch = files.slice(start, start + PREPARE_BATCH_SIZE)
        setProgress(
          `Preparing upload grants ${start + 1}-${Math.min(start + batch.length, files.length)} of ${files.length}…`,
        )
        const specs = []
        for (const item of batch) {
          // Hash one file at a time; do not retain all object bytes in memory.
          const buf = await item.file.arrayBuffer()
          specs.push({
            relative_path: item.relativePath,
            size_bytes: item.file.size,
            content_type: item.file.type || 'application/octet-stream',
            sha256: await sha256Hex(buf),
          })
        }

        const prepared = await api.prepareObjects(dropId, specs)
        const byPath = new Map(prepared.objects.map((o) => [o.relative_path, o]))

        for (let i = 0; i < batch.length; i++) {
          const item = batch[i]
          const grant = byPath.get(item.relativePath)
          if (!grant) throw new Error(`Missing upload grant for ${item.relativePath}`)
          const overall = start + i + 1
          setProgress(`Uploading ${overall}/${files.length}: ${item.relativePath}`)
          // Upload from File/Blob so the browser can stream without a second retained copy of every file.
          await putWithWriteSas(
            grant.write_sas_url,
            item.file,
            item.file.type || 'application/octet-stream',
          )
        }
      }

      setProgress('Finalizing…')
      const fin = await api.finalizeDrop(dropId)
      const localShare = `${window.location.origin}/d/${dropId}`
      setShareUrl(fin.share_url?.includes('/d/') ? fin.share_url : localShare)
      setProgress(null)
      navigate(`/drops/${dropId}`, { replace: true, state: { shareUrl: localShare } })
    } catch (err) {
      setError(errorMessage(err))
      setProgress(null)
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-sm text-slate-500">Loading…</p>
      </PageShell>
    )
  }
  if (!user) return <Navigate to="/login" replace />
  if (!user.mfa_enrolled) return <Navigate to="/mfa/setup" replace />

  return (
    <PageShell homeTo="/drops">
      <div className="mb-4">
        <Title>New drop</Title>
        <Subtitle>
          <Link to="/drops" className="underline">
            Back to list
          </Link>
        </Subtitle>
      </div>

      <Card className="mx-auto max-w-2xl">
        <form className="space-y-5" onSubmit={onSubmit}>
          {error ? <Alert>{error}</Alert> : null}
          {progress ? <Alert tone="info">{progress}</Alert> : null}
          {shareUrl ? (
            <Alert tone="success">
              Share link ready (do not share long-lived storage URLs):{' '}
              <span className="break-all font-mono text-xs">{shareUrl}</span>
            </Alert>
          ) : null}

          <div>
            <Label htmlFor="phone">Recipient phone</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              placeholder="+15551234567"
              required
              value={phone}
              onChange={(ev) => setPhone(ev.target.value)}
              aria-invalid={phone.length > 0 && !phoneOk}
            />
            <FieldError>
              {phone.length > 0 && !phoneOk
                ? 'Use E.164 (+country and number). US 10-digit numbers are accepted.'
                : 'Required. We only show the last four digits after create.'}
            </FieldError>
          </div>

          <div>
            <Label htmlFor="ttl">Available for (hours)</Label>
            <Input
              id="ttl"
              name="ttl"
              type="number"
              min={MIN_TTL}
              max={MAX_TTL}
              value={ttl}
              onChange={(ev) => setTtl(Number(ev.target.value))}
            />
            <FieldError>
              Default {DEFAULT_TTL}h. Allowed range {MIN_TTL}–{MAX_TTL} hours (up to 14 days).
            </FieldError>
          </div>

          <div>
            <Label htmlFor="files">Files and folders</Label>
            <div
              className={`rounded-xl border-2 border-dashed px-4 py-8 text-center ${
                dragOver ? 'border-[var(--brand-navy)] bg-slate-100' : 'border-slate-300 bg-slate-50'
              }`}
              onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => void onDropZone(e)}
            >
              <p className="mb-3 text-sm text-slate-600">
                Drag and drop files or folders here, or choose files / a folder. Relative paths are
                preserved.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose files
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => folderInputRef.current?.click()}
                >
                  Choose folder
                </Button>
              </div>
              <input
                ref={fileInputRef}
                id="files"
                type="file"
                multiple
                className="hidden"
                onChange={(ev) => {
                  if (ev.target.files) addFiles(ev.target.files)
                  ev.target.value = ''
                }}
              />
              <input
                ref={(el) => {
                  folderInputRef.current = el
                  if (el) {
                    el.setAttribute('webkitdirectory', '')
                    el.setAttribute('directory', '')
                  }
                }}
                type="file"
                className="hidden"
                multiple
                onChange={(ev) => {
                  if (ev.target.files) addFiles(ev.target.files)
                  ev.target.value = ''
                }}
              />
            </div>
            {files.length > 0 ? (
              <div className="mt-3 max-h-56 overflow-auto rounded-lg border border-slate-200">
                <ul className="divide-y text-sm">
                  {files.map((f) => (
                    <li key={f.relativePath} className="flex justify-between gap-2 px-3 py-2">
                      <span className="break-all font-mono text-xs">{f.relativePath}</span>
                      <span className="shrink-0 text-slate-500">{formatBytes(f.file.size)}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  {files.length} file(s) · {formatBytes(totalBytes)}
                  <button
                    type="button"
                    className="ml-3 underline"
                    onClick={() => setFiles([])}
                  >
                    Clear
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <Button type="submit" disabled={busy} className="w-full">
            {busy ? 'Working…' : 'Create drop and upload'}
          </Button>
        </form>
      </Card>
    </PageShell>
  )
}
